import {
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import {
  EXPORT_DATA_TYPES,
  EXPORT_FIELDS_INCLUSION_TYPES,
  EXPORT_INCLUSION_TYPES,
  EXPORT_RELATED_FILTERS,
  type ExportFieldConfiguration,
} from "./types";
import { RadioOption } from "../RadioOptionsInput";
import { getCommonExportRatioOptions, getFilterRadioOption } from "./utils";
import { useProductFilterRenderer } from "./hooks/useProductFilterRenderer";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import { LookbackSelector } from "./LookbackSelector";
import {
  installExportFields,
  firmographicsExportFields,
} from "./exportConfigurationFields";

const TIME_SERIES_FIELDS = [
  { value: "hg_id", label: "HG Company ID" },
  { value: "product_id", label: "Product ID" },
  { value: "date_verified", label: "Date Verified" },
  { value: "weighted_score", label: "Weighted Score" },
  { value: "document_type_names", label: "Document Type" },
];

interface Props {
  isInstallFilterEmpty: boolean;
  installInclusion: EXPORT_INCLUSION_TYPES;
  setInstallInclusion?: (v: EXPORT_INCLUSION_TYPES) => void;
  installProductIds?: number[];
  setInstallProductIds?: (ids: number[]) => void;
  // universe-only props (optional):
  universeEnabled?: boolean;
  installLookbackMonths?: number | string;
  onInstallLookbackChange?: (value: number | string) => void;
  installFieldConfiguration?: ExportFieldConfiguration;
  onInstallFieldConfigChange?: (cfg: ExportFieldConfiguration) => void;
  installIncludeTimeSeries?: boolean;
  onInstallIncludeTimeSeriesChange?: (v: boolean) => void;
  timeSeriesLookbackMonths?: number | string;
  onTimeSeriesLookbackChange?: (value: number | string) => void;
  timeSeriesFieldConfiguration?: ExportFieldConfiguration;
  onTimeSeriesFieldConfigChange?: (cfg: ExportFieldConfiguration) => void;
  enforceMandatoryFields?: boolean;
}

export const InstallsExportOption: React.FC<Props> = ({
  isInstallFilterEmpty,
  installInclusion,
  setInstallInclusion,
  installProductIds = [],
  setInstallProductIds,
  universeEnabled,
  installLookbackMonths,
  onInstallLookbackChange,
  installFieldConfiguration,
  onInstallFieldConfigChange,
  installIncludeTimeSeries,
  onInstallIncludeTimeSeriesChange,
  timeSeriesLookbackMonths,
  onTimeSeriesLookbackChange,
  timeSeriesFieldConfiguration,
  onTimeSeriesFieldConfigChange,
  enforceMandatoryFields,
}) => {
  //setup options with basic values
  const option1 = getFilterRadioOption(
    EXPORT_DATA_TYPES.INSTALL,
    EXPORT_RELATED_FILTERS.PRODUCTS,
    isInstallFilterEmpty
  );
  const option2: RadioOption = {
    label: "Include only selected products",
    value: EXPORT_INCLUSION_TYPES.SELECTED,
  };
  const option3 = getCommonExportRatioOptions(EXPORT_DATA_TYPES.INSTALL);
  const options: RadioOption[] = [option1, option2, ...option3];

  const renderOptionContent = useProductFilterRenderer(
    installInclusion,
    installProductIds,
    (ids) => setInstallProductIds?.(ids)
  );

  const isDisabled = installInclusion === EXPORT_INCLUSION_TYPES.NONE;

  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Stack>
        <Typography variant="h5" fontWeight={500} fontSize={16}>
          Installs
        </Typography>
      </Stack>
      <ExportDataTypeInclusionSelector
        dataType={EXPORT_DATA_TYPES.INSTALL}
        options={options}
        inclusion={installInclusion}
        setInclusion={setInstallInclusion}
        renderOptionContent={renderOptionContent}
      />
      {universeEnabled && (
        <Stack spacing={2}>
          <FieldConfigurationSelector
            fields={installExportFields}
            firmographicsFields={firmographicsExportFields}
            value={
              installFieldConfiguration ?? {
                inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
                fields: [],
              }
            }
            onChange={onInstallFieldConfigChange ?? (() => {})}
            title="Product Installs Fields"
            checkboxLabel="Export All Product Installs Fields"
            requiredField={
              enforceMandatoryFields
                ? {
                    values: ["company_group_id", "product_id"],
                    note: "Note: Company Group ID and Product ID will always be included in your export for proper install identification.",
                  }
                : undefined
            }
            disabled={isDisabled}
          />

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Install Last Verified Date Range [Universe Only]
            </Typography>
            <Typography variant="body2">
              The default last date verified in Platform is 2 years. Configure
              here to expand the date look back range, including choosing all
              time detections. The company list will not show all companies
              returned for an expanded date range query.
            </Typography>
            <LookbackSelector
              value={installLookbackMonths ?? 24}
              onChange={onInstallLookbackChange ?? (() => {})}
              disabled={isDisabled}
              label="Last Verified Date"
            />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2">
              Time Series [Universe Only]
            </Typography>
            <Typography variant="body2">
              Please note, Time Series is an independent file that is heavily
              dependent on your Install file.
            </Typography>
            <Typography variant="body2">
              To receive a Time Series file, you must also be receiving an
              Install file. Any export filters applied to the Install file will
              also apply to the Time Series file.
            </Typography>
            <Typography variant="body2">
              Installs included in Time Series will match the Install Last
              Verified Date Range you set above. In addition, you will also need
              to specify the date range for the Time Series data itself. This
              covers which installs qualify to be included, and how much data on
              those installs we deliver.
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              What Time Series data would you like to see in the export?
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={installIncludeTimeSeries ?? false}
                  onChange={(e) =>
                    onInstallIncludeTimeSeriesChange?.(e.target.checked)
                  }
                  disabled={isDisabled}
                  data-testid="install-time-series-toggle"
                />
              }
              label="Include Time Series data"
            />
            <Stack spacing={2}>
              {installIncludeTimeSeries && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">
                    Last Verified Date Range
                  </Typography>
                  <LookbackSelector
                    value={timeSeriesLookbackMonths ?? "ALLTIME"}
                    onChange={onTimeSeriesLookbackChange ?? (() => {})}
                    disabled={isDisabled}
                    label="Last Verified Date Range"
                  />
                </Stack>
              )}
              <FieldConfigurationSelector
                fields={TIME_SERIES_FIELDS}
                value={
                  timeSeriesFieldConfiguration ?? {
                    inclusionType: EXPORT_FIELDS_INCLUSION_TYPES.ALL,
                    fields: [],
                  }
                }
                onChange={onTimeSeriesFieldConfigChange ?? (() => {})}
                title="Time Series Fields"
                checkboxLabel="Export All Time Series Fields"
                disabled={isDisabled || !installIncludeTimeSeries}
              />
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
